import type { InitHeaders } from '@bldsoft/cappie/transport/http'

import { createCappie } from '@bldsoft/cappie/api/wbs'
import {
  createWbsPolicies,
  requestPathname,
} from '@bldsoft/cappie/api/wbs/policies'
import { localStorageStore } from '@bldsoft/cappie/api/wbs/policies/web-storage'
import { createOtelMetrics } from '@bldsoft/cappie/otel'
import { restify, withObserve } from '@bldsoft/cappie/rest'
import {
  enhance,
  withDefaults,
  withTimeout,
} from '@bldsoft/cappie/transport/http'
import { createFetchHttpClient } from '@bldsoft/cappie/transport/http/fetch'
import { observe } from '@setplex/tria-api-adapter-nora'
import { v4 as uuidv4 } from 'uuid'
import { runtime, version } from '~/app/environment'
import { live } from '~/shared/effector'
import { model as opentelemetry } from '~/shared/opentelemetry'
import { withCache } from './cache'
import {
  HTTP_TIMEOUT,
  MAX_RETRIES,
  MAX_RETRY_DELAY,
  TOTAL_RETRY_LIMIT_MILLIS,
} from './index.h'
import {
  $mwLanguage,
  $mwPackageId,
  $mwPid,
  header,
  organizationIdHeader,
} from './model'

// temporary migration cleanup: buckets moved to cappie's own storage namespace
localStorage.removeItem('tria__request_policy_state')

/**
 * Path prefix Era serves the WBS API under, empty unless the backend switcher
 * enabled the proxy
 */
const proxy = globalThis.__ERA_WBS_PROXY__ || ''

/**
 * Static and dynamic headers for the WBS API.
 * Live values are read per request. Getters run per request.
 */
export const headers: InitHeaders = {
  'accept': 'application/json',

  // wbs/nora specific headers
  'mw-platform': 'WEB',
  'mw-app-name': header(runtime.app.name), // got from era
  'mw-device-type': runtime.device.type.toUpperCase(), // DESKTOP / MOBILE
  'mw-device-os': header(runtime.os.name), // Linux / Android / iOS / MacOS / WebOS / Tizen / FireOS / Windows
  'mw-device-os-version': header(runtime.os.version),
  'mw-device-app-version': header(version),
  'mw-supported-drm-providers': 'EZDRM', // EXPRESS_PLAY / EZDRM / IRDETO / PALLYCON
  'mw-supported-drm-types': () => header(runtime.drms.join(',')), // filled after EME detection
  'mw-pid': live($mwPid), // provider id
  'multiorg-Id': () => organizationIdHeader(runtime.app.organizationId), // id to separate apps of same pid
  'mw-package-id': live($mwPackageId), // package id
  'mw-language': live($mwLanguage), // locale language
  'mw-web-metadata': JSON.stringify({
    browser: runtime.browser.name,
    browserVersion: runtime.browser.version,
  }),
  'mw-idempotency-key': () => uuidv4(), // https://setplexapps.atlassian.net/wiki/spaces/MW/pages/2682454084/Client+App+Request+Policy
}

/**
 * Request and Retry Policies for the WBS API
 * @see https://setplexapps.atlassian.net/wiki/spaces/MW/pages/3430776834/Request+Policy
 * @see https://setplexapps.atlassian.net/wiki/spaces/MW/pages/2682454084/Retry+Policy
 */
const policies = createWbsPolicies({
  store: localStorageStore(),

  retry: {
    maxRetries: MAX_RETRIES,
    retryTimeouts: false,
    totalLimitMillis: TOTAL_RETRY_LIMIT_MILLIS,
    maxRetryDelay: MAX_RETRY_DELAY,
  },

  // cappie matches `/wbs/` by default, which misses the Era proxy prefix
  match(url) {
    const path = requestPathname(url)
    return (
      path.startsWith('/wbs/') ||
      (proxy !== '' && path.startsWith(proxy + '/wbs/'))
    )
  },
})

/**
 * Request Policy controller, to observe and reset backoff state
 */
export const { controller } = policies

/**
 * HTTP transport for the WBS API.
 * Cache sits outside the policy, so hits cost no budget,
 * and the policy sits outside the timeout, so every attempt gets its own.
 */
const http = enhance(
  createFetchHttpClient(),
  withDefaults({
    baseUrl: proxy + '/wbs',
    path: { platform: 'web' },
    headers,
  }),
  withCache,
  policies.middleware,
  withTimeout(HTTP_TIMEOUT)
)

const { middleware: withOtelMetrics } = createOtelMetrics({
  meter: live(opentelemetry.$httpMeter),
})

/**
 * WBS API client, used by the Nora adapter.
 * Cappie records HTTP OTel instruments; the adapter observes calls for
 * `api.events.http.*`
 */
export const { cappie: client } = createCappie({
  rest: restify(http, withOtelMetrics, withObserve(observe)),
})

// expose debug handlers for the request policy
Object.defineProperty(globalThis, 'DEBUG_POLICY', {
  get() {
    return {
      buckets() {
        return controller.getBuckets()
      },
      clear() {
        return controller.clear()
      },
    }
  },
})
